import { getResizedImgUrl } from '~/scriptData/common/interM2'

const decodeHtmlEntities = (text) => {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

/**
 * 에디터로 등록한 이미지와 비디오에 리사이징 파라미터를 적용하여 리턴한다.
 */
const convertHtmlImage = ({ innerText, imageResize, imageQuality, videoResize, videoWidth }) => {
  const decodedText = /&lt;.*&gt;/.test(innerText) ? decodeHtmlEntities(innerText) : innerText
  const parser = new DOMParser()
  const doc = parser.parseFromString(decodedText, 'text/html')

  const images = Array.from(doc.getElementsByTagName('img'))
  const videos = Array.from(doc.getElementsByTagName('video'))

  const imgInfoList = getImgInfo({
    quality: imageQuality,
    imageTagList: images,
    width: imageResize,
  })

  const videoInfoList = getVideoInfo({
    videoTagList: videos,
    videoResize,
    videoWidth
  })

  // 이미지 태그 교체
  imgInfoList?.forEach((item) => {
    const imgEl = Array.from(doc.getElementsByTagName('img')).find(
      el => el.outerHTML === item.originalImgTag
    )
    if (imgEl) {
      const wrapper = document.createElement('div')
      wrapper.innerHTML = getPictureTag(item)
      imgEl.replaceWith(wrapper.firstElementChild)
    }
  })

  // 비디오 태그 교체
  videoInfoList?.forEach((item) => {
    const videoEl = Array.from(doc.getElementsByTagName('video')).find(
      el => el.outerHTML === item.originalVideoTag || item.originalVideoTag.includes(el.outerHTML)
    )

    if (videoEl) {
      const wrapper = document.createElement('div')
      wrapper.innerHTML = getVideoTag(item)
      videoEl.replaceWith(wrapper.firstElementChild)
    }
  })

  return doc.body.innerHTML
}

const getImgInfo = ({ imageTagList, width, quality = null }) => {
  return imageTagList?.map((img) => {
    const src = img?.getAttribute('src')
    const pureImgSrc = src?.replace(/\/dims\/.*/, '')
    const resizeParam = getResizeParam(src)

    return {
      originalImgTag: img?.outerHTML,
      resizedSrc: getResizedImgUrl(pureImgSrc, resizeParam, quality, null),
      resizedAvifSrc: getResizedImgUrl(pureImgSrc, resizeParam, quality, 'avif'),
      resizedWebpSrc: getResizedImgUrl(pureImgSrc, resizeParam, quality, 'webp'),
      alt: img?.getAttribute('alt'),
      class: img?.getAttribute('class'),
      style: img?.getAttribute('style'),
    }
  })
}

const getVideoInfo = ({ videoTagList, videoResize, videoWidth }) => {
  const getCleanBaseSrc = (url) => {
    const match = url.match(/^(.*?\.(mp4|mov|webm))/i)
    return match ? match[1] : url
  }

  return videoTagList?.map((video) => {
    const parentTag = video.parentElement?.tagName?.toLowerCase() === 'p'
      ? video.parentElement.outerHTML
      : video.outerHTML // <p> 안에 있으면 <p> 전체를 originalVideoTag로 설정

    const styleAttr = video.getAttribute('data-force-style') || ''
    const forceResizeAttr = video.getAttribute('data-force-resize') || ''
    const classAttr = video.getAttribute('class') || ''
    const directSrc = video.getAttribute('src')
    const poster = video.getAttribute('poster')
    const resize = forceResizeAttr?.trim() || videoResize?.trim() || '_480p'

    const sources = []

    const sourceTags = Array.from(video.getElementsByTagName('source'))
    if (sourceTags.length > 0) {
      const rawSrc = sourceTags[0].getAttribute('src')
      const baseSrc = getCleanBaseSrc(rawSrc)
      sources.push({
        src: `${baseSrc}/xcdr/preset/${resize}/xcdr/preset/_mp42webm/options/video.codec=vp9/.webm/faststart/true`,
        type: 'video/webm'
      })
      sources.push({
        src: `${baseSrc}/xcdr/preset/${resize}`,
        type: 'video/mp4'
      })
    }

    if (!sources.length && directSrc) {
      const baseSrc = getCleanBaseSrc(directSrc)
      sources.push({
        src: `${baseSrc}/xcdr/preset/${resize}/xcdr/preset/_mp42webm/options/video.codec=vp9/.webm/faststart/true`,
        type: 'video/webm'
      })
      sources.push({
        src: `${baseSrc}/xcdr/preset/${resize}`,
        type: 'video/mp4'
      })
    }

    return {
      originalVideoTag: parentTag,
      sources,
      style: styleAttr || `width: ${videoWidth ?? '750px'}; height: auto;`,
      class: classAttr,
      poster,
    }
  })
}

const getPictureTag = (htmlObject) => {
  return `
  <picture>
    <source srcset="${htmlObject.resizedAvifSrc ?? ''}" type="image/avif" />
    <source srcset="${htmlObject.resizedWebpSrc ?? ''}" type="image/webp" />
    <img
      src="${htmlObject.resizedSrc ?? ''}"
      class="${htmlObject.class ?? ''}"
      alt="${htmlObject.alt ?? ''}"
      style="${htmlObject.style ?? ''}" />
  </picture>`
}

const getVideoTag = (videoObj) => {
  const sourceTags = videoObj.sources.map((source) => {
    return `<source src="${source.src}" type="${source.type}" />`
  }).join('\n    ')

  const posterAttr = videoObj.poster ? `poster="${videoObj.poster}"` : ''

  return `
  <video muted autoplay playsinline loop class="${videoObj.class}" style="${videoObj.style}" ${posterAttr}>
    ${sourceTags}
  </video>`
}

const getHeightRatio = (htmlCode) => {
  const doc = new DOMParser().parseFromString(htmlCode, 'text/html')
  const images = Array.from(doc.getElementsByTagName('img'))
  let totalHeightRatio = 0

  images.forEach((item) => {
    const ratio = parseFloat(item.getAttribute('data-image-ratio'))
    totalHeightRatio += isNaN(ratio) || ratio === 0 ? 0 : ratio
  })

  return totalHeightRatio
}

const getResizeParam = (src) => {
  const match = src.match(/resize\/(\d+)/)
  if (match) {
    return match[1]
  } else {
    return null
  }
}

export {
  convertHtmlImage,
  getHeightRatio
}
